import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  BarChart3, 
  Mail, 
  MailOpen, 
  MousePointerClick, 
  Send,
  Users,
  AlertTriangle,
  Check,
  X,
  Loader2,
  Search,
  ExternalLink,
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewsletterReportPage() {
  const { campaignId } = useParams();
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [stats, setStats] = useState({});
  const [recipients, setRecipients] = useState([]);
  const [clickBreakdown, setClickBreakdown] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('sent_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  const [retrying, setRetrying] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  const getToken = () => localStorage.getItem('shrt_token');

  const handleSyncResend = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/newsletter/campaigns/${campaignId}/sync-resend`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(response.data.message);
      setTimeout(() => fetchReport(), 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  const handleRetryFailed = async () => {
    if (!window.confirm(`Fehlgeschlagene E-Mails (${stats.failed}) erneut senden?`)) return;
    setRetrying(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/newsletter/campaigns/${campaignId}/retry-failed`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(response.data.message);
      setTimeout(() => fetchReport(), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim erneuten Senden');
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [campaignId]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/newsletter/analytics/campaigns/${campaignId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setCampaign(response.data.campaign);
      setStats(response.data.stats || {});
      setRecipients(response.data.recipients || []);
      setClickBreakdown(response.data.click_breakdown || {});
    } catch (error) {
      toast.error('Bericht nicht gefunden');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const sortedRecipients = [...recipients]
    .filter(r => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return r.name?.toLowerCase().includes(query) || r.email?.toLowerCase().includes(query);
    })
    .sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      if (sortBy.includes('_at') || sortBy === 'open_count' || sortBy === 'click_count') {
        aVal = aVal || 0;
        bVal = bVal || 0;
        if (sortBy.includes('_at')) {
          aVal = aVal ? new Date(aVal).getTime() : 0;
          bVal = bVal ? new Date(bVal).getTime() : 0;
        }
      }
      
      if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!campaign) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Kampagne nicht gefunden</p>
          <Link to="/admin/newsletter-system">
            <Button className="mt-4 rounded-none">Zurück zur Übersicht</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/admin/newsletter-system" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="font-['Oswald'] text-2xl font-bold text-foreground uppercase tracking-tight">
              {campaign.name}
            </h1>
            <p className="text-sm text-muted-foreground">
              Betreff: {campaign.subject}
            </p>
          </div>
        </div>

        {/* Campaign Info */}
        <div className="bg-card border border-border p-4 flex flex-wrap gap-6 text-sm">
          <div>
            <span className="text-muted-foreground">Gesendet am:</span>
            <span className="ml-2 font-medium">{formatDate(campaign.sent_at)}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Erstellt von:</span>
            <span className="ml-2 font-medium">{campaign.created_by}</span>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-card border border-border p-4 text-center">
            <Users className="mx-auto mb-2 text-muted-foreground" size={24} />
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground uppercase">Empfänger</p>
          </div>
          
          <div className="bg-card border border-border p-4 text-center">
            <Send className="mx-auto mb-2 text-blue-500" size={24} />
            <p className="text-2xl font-bold text-blue-500">{stats.sent}</p>
            <p className="text-xs text-muted-foreground uppercase">Gesendet</p>
          </div>
          
          <div className="bg-green-500/10 border border-green-500/30 p-4 text-center">
            <MailOpen className="mx-auto mb-2 text-green-500" size={24} />
            <p className="text-2xl font-bold text-green-500">{stats.opened}</p>
            <p className="text-xs text-muted-foreground uppercase">Geöffnet</p>
            <p className="text-sm font-semibold text-green-400">{stats.open_rate}%</p>
          </div>
          
          <div className="bg-purple-500/10 border border-purple-500/30 p-4 text-center">
            <MousePointerClick className="mx-auto mb-2 text-purple-500" size={24} />
            <p className="text-2xl font-bold text-purple-500">{stats.clicked}</p>
            <p className="text-xs text-muted-foreground uppercase">Geklickt</p>
            <p className="text-sm font-semibold text-purple-400">{stats.click_rate}%</p>
          </div>
          
          <div className="bg-card border border-border p-4 text-center">
            <AlertTriangle className="mx-auto mb-2 text-orange-500" size={24} />
            <p className="text-2xl font-bold text-orange-500">{(stats.failed || 0) + (stats.bounced || 0)}</p>
            <p className="text-xs text-muted-foreground uppercase">Fehlgeschlagen</p>
            <div className="flex flex-col gap-1 mt-2">
              {((stats.failed || 0) + (stats.bounced || 0)) > 0 && (
                <Button 
                  size="sm" 
                  variant="outline"
                  className="text-xs border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                  onClick={handleRetryFailed}
                  disabled={retrying}
                  data-testid="retry-failed-btn"
                >
                  {retrying ? <Loader2 size={12} className="animate-spin mr-1" /> : <Send size={12} className="mr-1" />}
                  Erneut senden
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="text-xs border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
                onClick={handleSyncResend}
                disabled={syncing}
                data-testid="sync-resend-btn"
              >
                {syncing ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
                Sync mit Resend
              </Button>
            </div>
          </div>
        </div>

        {/* Click Breakdown */}
        {Object.keys(clickBreakdown).length > 0 && (
          <div className="bg-card border border-border p-6">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <MousePointerClick size={18} />
              Klick-Analyse
            </h2>
            <div className="space-y-2">
              {Object.entries(clickBreakdown)
                .sort((a, b) => b[1] - a[1])
                .map(([url, count]) => (
                  <div key={url} className="flex items-center justify-between p-3 bg-muted/20 rounded">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <ExternalLink size={14} className="text-muted-foreground flex-shrink-0" />
                      <span className="text-sm truncate">{url}</span>
                    </div>
                    <span className="font-semibold text-purple-500 ml-4">{count} Klicks</span>
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {/* Recipients List */}
        <div className="bg-card border border-border">
          <div className="p-4 border-b border-border flex flex-col sm:flex-row gap-3">
            <h2 className="font-semibold flex items-center gap-2">
              <Users size={18} />
              Empfänger ({recipients.length})
            </h2>
            <div className="relative flex-1 sm:max-w-xs ml-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Suchen..."
                className="pl-10 rounded-none h-9"
              />
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/30">
                <tr>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Empfänger
                      <SortIcon field="name" />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                    onClick={() => handleSort('sent_at')}
                  >
                    <div className="flex items-center gap-1">
                      Gesendet
                      <SortIcon field="sent_at" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                    onClick={() => handleSort('opened_at')}
                  >
                    <div className="flex items-center gap-1">
                      <MailOpen size={12} />
                      Geöffnet
                      <SortIcon field="opened_at" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                    onClick={() => handleSort('open_count')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Öffnungen
                      <SortIcon field="open_count" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                    onClick={() => handleSort('clicked_at')}
                  >
                    <div className="flex items-center gap-1">
                      <MousePointerClick size={12} />
                      Geklickt
                      <SortIcon field="clicked_at" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sortedRecipients.map((recipient) => (
                  <tr key={recipient.id} className="hover:bg-muted/10">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-foreground">{recipient.name}</p>
                        <p className="text-xs text-muted-foreground">{recipient.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {recipient.status === 'sent' || recipient.status === 'delivered' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-500/20 text-green-400">
                          <Check size={12} />
                          {recipient.status === 'delivered' ? 'Zugestellt' : 'Gesendet'}
                        </span>
                      ) : recipient.status === 'bounced' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-orange-500/20 text-orange-400">
                          <AlertTriangle size={12} />
                          Bounce
                        </span>
                      ) : recipient.status === 'failed' ? (
                        <div>
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-500/20 text-red-400">
                            <X size={12} />
                            Fehlgeschlagen
                          </span>
                          {recipient.error && (
                            <p className="text-xs text-red-400/70 mt-1 max-w-xs truncate" title={recipient.error}>
                              {recipient.error}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-muted text-muted-foreground">
                          <Clock size={12} />
                          Ausstehend
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatDate(recipient.sent_at)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {recipient.opened_at ? (
                        <span className="text-green-400">{formatDate(recipient.opened_at)}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium">
                      {recipient.open_count || 0}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {recipient.clicked_at ? (
                        <span className="text-purple-400">{formatDate(recipient.clicked_at)}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
